import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Location } from '@angular/common';

export interface LegacyUrlParams {
  event?: string;
  uR?: string;
  rname?: string;
  cm1?: string;
  id?: string;
}

@Injectable({
  providedIn: 'root'
})
export class RedirectService {
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private location = inject(Location);

  /**
   * Handles legacy URLs from the old system
   * @param url The full legacy URL to parse and handle
   * @returns A boolean indicating if the URL was successfully handled
   */
  handleLegacyUrl(url: string): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      console.log('Server-side environment detected, skipping client-side URL handling');
      return false;
    }
    
    try {
      const urlObj = new URL(url);
      const params = Object.fromEntries(urlObj.searchParams.entries());
      
      if (urlObj.pathname.includes('/i2report/index.cfm')) {
        return this.handleLegacyReportUrl(params as LegacyUrlParams);
      }
      
      return false;
    } catch (error) {
      console.error('Error parsing legacy URL:', error);
      return false;
    }
  }

  /**
   * Handles legacy report URLs
   * @param params The parameters extracted from the legacy URL
   * @returns A boolean indicating if the URL was successfully handled
   */
  private handleLegacyReportUrl(params: LegacyUrlParams): boolean {
    if (params.event === 'view.news' && params.uR) {
      // Ensure all required parameters are present in the URL
      const queryParams = new URLSearchParams();
      
      if (params.event) queryParams.set('event', params.event);
      if (params.uR) queryParams.set('uR', params.uR);
      if (params.rname) queryParams.set('rname', params.rname);
      if (params.cm1) queryParams.set('cm1', params.cm1);
      if (params.id) queryParams.set('id', params.id);

      // Maintain the legacy URL format
      const legacyUrl = `/i2report/index.cfm?${queryParams.toString()}`;
      
      // Use replaceState to update the URL without triggering a navigation
      this.location.replaceState(legacyUrl);
      
      // Track the legacy URL access (optional)
      this.trackLegacyUrlAccess(params);
      
      return true;
    }
    
    return false;
  }
  
  /**
   * Tracks legacy URL access for analytics
   * @param params The parameters from the legacy URL
   */
  private trackLegacyUrlAccess(params: LegacyUrlParams): void {
    // Implement analytics tracking here if needed
    console.log('Legacy URL accessed with params:', params);
  }
} 